import { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  claveBancoCatalogo,
  configPagoProveedorCompleta,
  configPagoProveedorVacia,
  esMedioTransferenciaProv,
  etiquetaCuentaOrigen,
  excedenteRetencionPreview,
  formatCuit,
  obtenerImpuestosFactura,
  obtenerResumenPreview,
  obtenerTotalesOtrosImpuestosFactura,
  OPCIONES_MEDIO_PAGO_PROVEEDOR,
  proveedorPuedeTransferencia,
  requiereMediosPago,
  resumenImportesSeleccion,
} from "./utils";
import ImpuestosFacturaPanel from "./ImpuestosFacturaPanel";

const thSx = {
  bgcolor: "#2471A3",
  color: "#fff",
  fontWeight: 600,
  whiteSpace: "nowrap",
  py: 0.75,
  fontSize: 12,
  lineHeight: 1.3,
};

const FilaTotal = ({
  label,
  valor,
  destacado = false,
  resta = false,
  alerta = false,
}) => {
  const colorDestacado = alerta ? "error.main" : "primary.main";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        py: 0.25,
      }}
    >
      <Typography
        color={destacado ? colorDestacado : "text.secondary"}
        fontWeight={destacado ? 600 : 400}
        sx={{
          fontSize: destacado ? "0.8rem" : "0.72rem",
          lineHeight: 1.4,
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        fontWeight={destacado ? 700 : 600}
        color={
          destacado ? colorDestacado : resta ? "error.main" : "text.primary"
        }
        sx={{
          fontSize: destacado ? "0.95rem" : "0.75rem",
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {resta && valor !== "—" ? `− ${valor}` : valor}
      </Typography>
    </Box>
  );
};

const PanelDesgloseComprobantes = ({
  desglose,
  subtotalApi,
  formatMoneda,
}) => {
  if (!desglose?.tieneNotasCredito) {
    return (
      <FilaTotal
        label="Total comprobantes"
        valor={formatMoneda(subtotalApi)}
      />
    );
  }

  return (
    <>
      <FilaTotal
        label="Total comprobantes a pagar"
        valor={formatMoneda(desglose.totalDebitos)}
      />
      <FilaTotal
        label="(−) Notas de crédito"
        valor={formatMoneda(desglose.totalNotasCredito)}
        resta
      />
      <FilaTotal
        label="(=) Subtotal comprobantes"
        valor={formatMoneda(subtotalApi)}
      />
    </>
  );
};

const detalleRetencion = (item, formatMoneda) => {
  const alicuota =
    item.alicuota_efectiva ??
    item.alicuota ??
    (item.base > 0
      ? Math.round((Number(item.importe) / Number(item.base)) * 10000) / 100
      : null);

  if (alicuota != null && alicuota > 0) {
    return `${alicuota}% sobre ${formatMoneda(item.base)}`;
  }
  return `Sobre ${formatMoneda(item.base)}`;
};

const FilaRetencion = ({ label, detalle, valor, formatMoneda }) => (
  <Box sx={{ py: 0.25 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
      <Typography
        fontWeight={500}
        sx={{ fontSize: "0.72rem", lineHeight: 1.4, minWidth: 0 }}
      >
        {label}
      </Typography>
      <Typography
        fontWeight={600}
        sx={{
          fontSize: "0.72rem",
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {formatMoneda(valor)}
      </Typography>
    </Box>
    {detalle && (
      <Typography
        color="text.secondary"
        display="block"
        sx={{ fontSize: "0.65rem", lineHeight: 1.3 }}
      >
        {detalle}
      </Typography>
    )}
  </Box>
);

/**
 * Aclara por qué la retención no cierra contra el importe de la fila: la base es
 * el neto completo de la factura y los pagos parciales no retienen.
 */
const NotaBaseRetencion = ({ resumen }) => {
  const sinRetencion = Number(resumen?.cantidad_facturas_sin_retencion) || 0;
  const conRetencion = Number(resumen?.cantidad_facturas_con_retencion) || 0;

  if (sinRetencion === 0 && conRetencion === 0) return null;

  return (
    <Box sx={{ mb: 0.5 }}>
      {sinRetencion > 0 ? (
        <Typography
          color="text.secondary"
          display="block"
          sx={{ fontSize: "0.65rem", lineHeight: 1.3 }}
        >
          {sinRetencion === 1
            ? "1 comprobante queda con pago parcial: no retiene hasta que se cancele."
            : `${sinRetencion} comprobantes quedan con pago parcial: no retienen hasta que se cancelen.`}
        </Typography>
      ) : null}
      {conRetencion > 0 ? (
        <Typography
          color="text.secondary"
          display="block"
          sx={{ fontSize: "0.65rem", lineHeight: 1.3 }}
        >
          Base de cálculo: neto total de la factura, no el importe de este pago.
        </Typography>
      ) : null}
    </Box>
  );
};

const DetalleRetencionesProveedor = ({
  retenciones,
  proveedor,
  resumen,
  detalle,
  advertencias = [],
  formatMoneda,
  compacto = false,
}) => {
  const resumenDetalle = detalle?.resumen;
  const ganancias = retenciones?.ganancias;
  const iva = retenciones?.iva;
  const iibb = retenciones?.iibb ?? [];
  const hayIva =
    iva &&
    (iva.aplica != null ? Boolean(iva.aplica) : Number(iva.importe) > 0) &&
    Number(iva.importe) > 0;
  const hayAlgo =
    Boolean(ganancias?.aplica) ||
    hayIva ||
    iibb.some((item) => Number(item.importe) > 0);

  return (
    <Box>
      {proveedor?.condicion_ganancias && !compacto ? (
        <Typography
          color="text.secondary"
          display="block"
          sx={{ mb: 0.5, fontSize: "0.65rem", lineHeight: 1.3 }}
        >
          Ganancias: {proveedor.condicion_ganancias}
          {proveedor.codigo_ganancias_padron
            ? ` (${proveedor.codigo_ganancias_padron})`
            : ""}
        </Typography>
      ) : null}

      <NotaBaseRetencion resumen={resumen} />

      {ganancias?.aplica && (
        <FilaRetencion
          label="Retención Ganancias"
          detalle={
            ganancias.alicuota != null
              ? `${ganancias.alicuota}% sobre ${formatMoneda(ganancias.base)}`
              : detalleRetencion(ganancias, formatMoneda)
          }
          valor={ganancias.importe}
          formatMoneda={formatMoneda}
        />
      )}

      {ganancias && !ganancias.aplica && ganancias.mensaje && (
        <Typography
          color="text.secondary"
          display="block"
          sx={{ mb: 0.5, fontSize: "0.65rem", lineHeight: 1.3 }}
        >
          Ganancias: {ganancias.mensaje}
        </Typography>
      )}

      {hayIva ? (
        <FilaRetencion
          label="Retención IVA"
          detalle={detalleRetencion(iva, formatMoneda)}
          valor={iva.importe}
          formatMoneda={formatMoneda}
        />
      ) : null}

      {iibb.map((item) =>
        Number(item.importe) > 0 ? (
          <FilaRetencion
            key={item.id ?? item.id_provincia ?? item.certificado}
            label={item.certificado || `IIBB ${item.provincia || ""}`.trim()}
            detalle={detalleRetencion(item, formatMoneda)}
            valor={item.importe}
            formatMoneda={formatMoneda}
          />
        ) : null
      )}

      {!hayAlgo ? (
        <Typography
          color="text.secondary"
          sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}
        >
          Sin retenciones calculadas.
        </Typography>
      ) : null}

      {resumenDetalle ? (
        <>
          <Divider sx={{ my: 0.5 }} />
          {(resumenDetalle.total_retenciones_ganancias ?? 0) > 0 && (
            <FilaTotal
              label="Total Ganancias"
              valor={formatMoneda(resumenDetalle.total_retenciones_ganancias)}
            />
          )}
          {(resumenDetalle.total_retenciones_iva ?? 0) > 0 && (
            <FilaTotal
              label="Total IVA"
              valor={formatMoneda(resumenDetalle.total_retenciones_iva)}
            />
          )}
          {(resumenDetalle.total_retenciones_iibb ?? 0) > 0 && (
            <FilaTotal
              label="Total Ing. Brutos"
              valor={formatMoneda(resumenDetalle.total_retenciones_iibb)}
            />
          )}
          {(resumenDetalle.total_retenciones ?? 0) > 0 && (
            <FilaTotal
              label="Total retenciones"
              valor={formatMoneda(resumenDetalle.total_retenciones)}
            />
          )}
        </>
      ) : (retenciones?.total ?? 0) > 0 ? (
        <>
          <Divider sx={{ my: 0.5 }} />
          <FilaTotal
            label="Total retenciones"
            valor={formatMoneda(retenciones.total)}
          />
        </>
      ) : null}

      {advertencias?.length > 0 &&
        advertencias.map((msg, i) => (
          <Alert key={i} severity="warning" sx={{ mt: 1 }}>
            {msg}
          </Alert>
        ))}
    </Box>
  );
};

const PanelRetenciones = ({
  preview,
  previewLoading,
  previewError,
  formatMoneda,
  haySeleccion,
}) => {
  if (!haySeleccion) {
    return (
      <Typography
        color="text.secondary"
        textAlign="center"
        sx={{ py: 0.5, fontSize: "0.72rem", lineHeight: 1.4 }}
      >
        Seleccione comprobantes para calcular retenciones.
      </Typography>
    );
  }

  if (previewLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (previewError) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {previewError}
      </Alert>
    );
  }

  if (!preview) return null;

  const grupos = Array.isArray(preview.grupos) ? preview.grupos : [];
  if (preview.multi_proveedor && grupos.length > 1) {
    const totalRet = obtenerResumenPreview(preview)?.total_retenciones ?? 0;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {grupos.map((grupo) => {
          const nombre =
            grupo.razon_social ||
            grupo.proveedor?.razon_social ||
            formatCuit(grupo.cuit) ||
            "Proveedor";
          const totalGrupo =
            Number(
              grupo.resumen?.total_retenciones ?? grupo.retenciones?.total ?? 0
            ) || 0;

          return (
            <Box
              key={String(grupo.cuit)}
              sx={{
                p: 1,
                borderRadius: 1,
                border: "1px solid #e8e8e8",
                bgcolor: "#fafbfc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  fontWeight={700}
                  sx={{ fontSize: "0.78rem", lineHeight: 1.3 }}
                  title={nombre}
                >
                  {nombre}
                </Typography>
                <Typography
                  fontWeight={700}
                  color="primary.main"
                  sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}
                >
                  {formatMoneda(totalGrupo)}
                </Typography>
              </Box>
              <Typography
                color="text.secondary"
                sx={{ fontSize: "0.6rem", mb: 0.5 }}
              >
                {formatCuit(grupo.cuit)}
              </Typography>
              <DetalleRetencionesProveedor
                retenciones={grupo.retenciones}
                proveedor={grupo.proveedor}
                resumen={grupo.resumen}
                detalle={grupo.detalle}
                advertencias={grupo.advertencias}
                formatMoneda={formatMoneda}
                compacto
              />
            </Box>
          );
        })}

        <Divider sx={{ my: 0.25 }} />
        <FilaTotal
          label="Total retenciones"
          valor={formatMoneda(totalRet)}
          destacado
        />

        {(preview.advertencias?.length ?? 0) > 0 &&
          preview.advertencias.map((msg, i) => (
            <Alert key={i} severity="warning" sx={{ mt: 0.5 }}>
              {msg}
            </Alert>
          ))}
      </Box>
    );
  }

  return (
    <DetalleRetencionesProveedor
      retenciones={preview.retenciones}
      proveedor={preview.proveedor}
      resumen={preview.resumen}
      detalle={preview.detalle}
      advertencias={preview.advertencias}
      formatMoneda={formatMoneda}
    />
  );
};

const selectCompactSx = {
  fontSize: "0.72rem",
  height: 26,
  "& .MuiSelect-select": {
    py: 0,
    px: 1.25,
    fontSize: "0.72rem",
    lineHeight: "26px",
    display: "flex",
    alignItems: "center",
    minHeight: "26px !important",
    height: 26,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,0,0,0.23)",
  },
};

const labelCompactSx = {
  fontSize: "0.72rem",
  lineHeight: 1,
  transform: "translate(12px, 6px) scale(1)",
  "&.MuiInputLabel-shrink": {
    fontSize: "0.75rem",
    transform: "translate(14px, -8px) scale(0.75)",
  },
};

const menuItemCompactSx = { fontSize: "0.72rem", py: 0.5, minHeight: 28 };

const SelectoresPagoFila = ({
  config,
  onChange,
  bancosUnicos = [],
  cuentasBancarias = [],
  loadingCuentas = false,
  incompleto = false,
  puedeTransferencia = true,
}) => {
  const medio = config?.medio || "";
  const esTransferencia = esMedioTransferenciaProv(medio);
  const cuentasDelBanco = esTransferencia
    ? (cuentasBancarias || []).filter(
        (c) => claveBancoCatalogo(c) === (config?.codigoBanco || "")
      )
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        py: 0,
        width: "100%",
      }}
    >
      <FormControl
        size="small"
        error={incompleto && !medio}
        sx={{ width: "100%" }}
      >
        <InputLabel sx={labelCompactSx}>Medio</InputLabel>
        <Select
          label="Medio"
          value={medio}
          sx={selectCompactSx}
          onChange={(e) => {
            const nextMedio = e.target.value;
            if (esMedioTransferenciaProv(nextMedio) && !puedeTransferencia) {
              return;
            }
            onChange({
              ...(config || configPagoProveedorVacia()),
              medio: nextMedio,
              ...(esMedioTransferenciaProv(nextMedio)
                ? {}
                : { codigoBanco: "", id_bancos_cuentas: "" }),
            });
          }}
        >
          <MenuItem value="" sx={menuItemCompactSx}>
            Sin definir
          </MenuItem>
          {OPCIONES_MEDIO_PAGO_PROVEEDOR.map((op) => (
            <MenuItem
              key={op.value}
              value={op.value}
              sx={menuItemCompactSx}
              disabled={
                esMedioTransferenciaProv(op.value) && !puedeTransferencia
              }
            >
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {esTransferencia ? (
        <>
          <FormControl
            size="small"
            error={incompleto && !config?.codigoBanco}
            disabled={loadingCuentas}
            sx={{ width: "100%" }}
          >
            <InputLabel sx={labelCompactSx}>Banco</InputLabel>
            <Select
              label="Banco"
              value={config?.codigoBanco || ""}
              sx={selectCompactSx}
              onChange={(e) => {
                const codigoBanco = e.target.value;
                const cuentas = (cuentasBancarias || []).filter(
                  (c) => claveBancoCatalogo(c) === codigoBanco
                );
                onChange({
                  ...(config || configPagoProveedorVacia()),
                  medio,
                  codigoBanco,
                  id_bancos_cuentas:
                    cuentas.length === 1 ? cuentas[0].id_bancos_cuentas : "",
                });
              }}
            >
              {bancosUnicos.map((b) => (
                <MenuItem key={b.codigo} value={b.codigo} sx={menuItemCompactSx}>
                  {b.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            error={incompleto && !config?.id_bancos_cuentas}
            disabled={loadingCuentas || !config?.codigoBanco}
            sx={{ width: "100%" }}
          >
            <InputLabel sx={labelCompactSx}>Cuenta</InputLabel>
            <Select
              label="Cuenta"
              value={config?.id_bancos_cuentas || ""}
              sx={selectCompactSx}
              renderValue={(val) => {
                const c = cuentasDelBanco.find(
                  (x) => String(x.id_bancos_cuentas) === String(val)
                );
                if (!c) return val;
                return c.nro_cuenta || etiquetaCuentaOrigen(c);
              }}
              onChange={(e) =>
                onChange({
                  ...(config || configPagoProveedorVacia()),
                  medio,
                  codigoBanco: config?.codigoBanco || "",
                  id_bancos_cuentas: e.target.value,
                })
              }
            >
              {cuentasDelBanco.map((c) => (
                <MenuItem
                  key={c.id_bancos_cuentas}
                  value={c.id_bancos_cuentas}
                  sx={menuItemCompactSx}
                >
                  {c.nro_cuenta
                    ? `${c.nro_cuenta}${c.cbu ? ` — ${c.cbu}` : ""}`
                    : etiquetaCuentaOrigen(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : medio ? (
        <Typography
          color="text.secondary"
          sx={{ fontSize: "0.68rem", lineHeight: 1.3 }}
        >
          Banco: —
        </Typography>
      ) : null}
    </Box>
  );
};

const PanelMedioPagoProveedores = ({
  proveedoresSeleccionados = [],
  configPagoPorProveedor = {},
  onConfigPagoChange,
  cuentasBancarias = [],
  loadingCuentas = false,
}) => {
  const bancosUnicos = useMemo(() => {
    const map = new Map();
    (cuentasBancarias || []).forEach((c) => {
      const key = claveBancoCatalogo(c);
      if (!key || map.has(key)) return;
      map.set(key, {
        codigo: key,
        nombre: c.razon_social || c.codigo || key,
      });
    });
    return Array.from(map.values());
  }, [cuentasBancarias]);

  if (!proveedoresSeleccionados.length) {
    return (
      <Typography
        color="text.secondary"
        textAlign="center"
        sx={{ py: 0.5, fontSize: "0.72rem", lineHeight: 1.4 }}
      >
        Seleccioná comprobantes para definir el medio de pago por proveedor.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>Proveedor</TableCell>
            <TableCell sx={{ ...thSx, width: "52%" }}>Medio / Banco</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {proveedoresSeleccionados.map((prov) => {
            const cuit = String(prov.cuit || "");
            const config =
              configPagoPorProveedor?.[cuit] || configPagoProveedorVacia();
            const puedeTransferencia = proveedorPuedeTransferencia(prov);
            const incompleto = !configPagoProveedorCompleta(config, prov);

            return (
              <TableRow
                key={cuit}
                sx={{
                  bgcolor: incompleto ? "rgba(237, 108, 2, 0.06)" : undefined,
                  "& td": { verticalAlign: "middle", py: 0.5, px: 1 },
                }}
              >
                <TableCell sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}
                    title={prov.razon_social || ""}
                  >
                    {prov.razon_social || "Sin razón social"}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "0.6rem", lineHeight: 1.25 }}
                  >
                    {formatCuit(cuit)}
                  </Typography>
                  {incompleto ? (
                    <Typography
                      color="warning.main"
                      sx={{ fontSize: "0.6rem", mt: 0.25, fontWeight: 600 }}
                    >
                      Completar
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ width: "52%" }}>
                  <SelectoresPagoFila
                    config={config}
                    onChange={(next) => onConfigPagoChange?.(cuit, next)}
                    bancosUnicos={bancosUnicos}
                    cuentasBancarias={cuentasBancarias}
                    loadingCuentas={loadingCuentas}
                    incompleto={incompleto}
                    puedeTransferencia={puedeTransferencia}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ResumenSeleccion = ({
  preview,
  previewLoading,
  previewError,
  formatMoneda,
  cantidadSeleccionada = 0,
  filasSeleccionadas = [],
  importesAPagar = {},
  cantidadOpsEstimada = 1,
  mensajeOpsEstimada = "",
  proveedoresSeleccionados = [],
  configPagoPorProveedor = {},
  onConfigPagoChange,
  cuentasBancarias = [],
  loadingCuentas = false,
  mostrarConfigPago = false,
  mostrarRetenciones = true,
  mostrarResumenPago = true,
}) => {
  const haySeleccion = cantidadSeleccionada > 0;
  const resumen = obtenerResumenPreview(preview);
  const filasResumen =
    filasSeleccionadas.length > 0
      ? filasSeleccionadas
      : (preview?.comprobantes ?? []);
  const desglose = resumenImportesSeleccion(filasResumen, importesAPagar);
  const impuestosFactura = obtenerImpuestosFactura(preview);
  const totalesOtrosImpuestos = obtenerTotalesOtrosImpuestosFactura(preview);
  const excedenteRetencion = excedenteRetencionPreview(preview);
  const sinImporteNeto =
    resumen?.total_a_pagar != null && !requiereMediosPago(resumen.total_a_pagar);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
      {cantidadOpsEstimada > 1 ? (
        <Alert severity="info" icon={false}>
          {mensajeOpsEstimada ||
            `Se generarán ${cantidadOpsEstimada} órdenes de pago (una por banco origen). El proveedor recibirá un solo correo con el detalle completo.`}
        </Alert>
      ) : null}

      {mostrarConfigPago ? (
        <Paper elevation={1} sx={{ p: 1.25, backgroundColor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            <PaymentIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Medio de pago por proveedor
            </Typography>
          </Box>
          <Typography
            color="text.secondary"
            sx={{ mb: 1, fontSize: "0.65rem", lineHeight: 1.35 }}
          >
            El medio no es obligatorio. Transferencia solo si el proveedor tiene
            banco y CBU. Cheque y e-cheque no son obligatorios.
          </Typography>
          <PanelMedioPagoProveedores
            proveedoresSeleccionados={proveedoresSeleccionados}
            configPagoPorProveedor={configPagoPorProveedor}
            onConfigPagoChange={onConfigPagoChange}
            cuentasBancarias={cuentasBancarias}
            loadingCuentas={loadingCuentas}
          />
        </Paper>
      ) : null}

      {mostrarRetenciones ? (
        <Paper elevation={1} sx={{ p: 1.25, backgroundColor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <AccountBalanceIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Retenciones
            </Typography>
          </Box>
          <PanelRetenciones
            preview={preview}
            previewLoading={previewLoading}
            previewError={previewError}
            formatMoneda={formatMoneda}
            haySeleccion={haySeleccion}
          />
        </Paper>
      ) : null}

      {haySeleccion && impuestosFactura.length > 0 ? (
        <Paper elevation={1} sx={{ p: 1.25, backgroundColor: "#fff" }}>
          <ImpuestosFacturaPanel
            impuestos={impuestosFactura}
            totales={totalesOtrosImpuestos}
            compact
          />
        </Paper>
      ) : null}

      {mostrarResumenPago ? (
        <Paper elevation={1} sx={{ p: 1.25, backgroundColor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
            <ReceiptLongIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Resumen del pago
            </Typography>
          </Box>

          {!haySeleccion ? (
            <Typography
              color="text.secondary"
              sx={{ py: 0.5, fontSize: "0.72rem", lineHeight: 1.4 }}
            >
              Seleccione uno o más comprobantes para ver el resumen.
            </Typography>
          ) : previewLoading && !resumen ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : resumen ? (
            <>
              <Typography
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5, fontSize: "0.65rem", lineHeight: 1.3 }}
              >
                {resumen.cantidad_comprobantes ?? cantidadSeleccionada} comprobante
                {(resumen.cantidad_comprobantes ?? cantidadSeleccionada) !== 1 ? "s" : ""}{" "}
                seleccionado
                {(resumen.cantidad_comprobantes ?? cantidadSeleccionada) !== 1 ? "s" : ""}
              </Typography>
              <PanelDesgloseComprobantes
                desglose={desglose}
                subtotalApi={resumen.total_facturas}
                formatMoneda={formatMoneda}
              />
              <FilaTotal label="Neto imponible" valor={formatMoneda(resumen.neto_imponible)} />
              {preview?.detalle?.resumen ? (
                <>
                  {(preview.detalle.resumen.total_retenciones_ganancias ?? 0) > 0 && (
                    <FilaTotal
                      label="(−) Ret. Ganancias"
                      valor={formatMoneda(preview.detalle.resumen.total_retenciones_ganancias)}
                      resta
                    />
                  )}
                  {(preview.detalle.resumen.total_retenciones_iva ?? 0) > 0 && (
                    <FilaTotal
                      label="(−) Ret. IVA"
                      valor={formatMoneda(preview.detalle.resumen.total_retenciones_iva)}
                      resta
                    />
                  )}
                  {(preview.detalle.resumen.total_retenciones_iibb ?? 0) > 0 && (
                    <FilaTotal
                      label="(−) Ret. Ing. Brutos"
                      valor={formatMoneda(preview.detalle.resumen.total_retenciones_iibb)}
                      resta
                    />
                  )}
                </>
              ) : null}
              {(resumen.total_retenciones ?? 0) > 0 && (
                <FilaTotal
                  label="(−) Total retenciones"
                  valor={formatMoneda(resumen.total_retenciones)}
                  resta
                />
              )}
              {(resumen.total_retenciones ?? 0) <= 0 && (
                <FilaTotal
                  label="Total retenciones"
                  valor={formatMoneda(resumen.total_retenciones)}
                />
              )}
              <Divider sx={{ my: 0.5 }} />
              <FilaTotal
                label="Total a pagar"
                valor={formatMoneda(resumen.total_a_pagar)}
                destacado
                alerta={sinImporteNeto}
              />
              {excedenteRetencion > 0 ? (
                <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0.5 }}>
                  <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}>
                    Las retenciones superan el importe a pagar. Quedan{" "}
                    {formatMoneda(excedenteRetencion)} a favor de la empresa y el
                    proveedor no recibe transferencia.
                  </Typography>
                </Alert>
              ) : sinImporteNeto ? (
                <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5 }}>
                  <Typography sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}>
                    No queda importe neto para transferir: la orden se registra sin
                    medios de pago.
                  </Typography>
                </Alert>
              ) : null}
            </>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
};

export default ResumenSeleccion;
